import { alpha, chipClasses, Components, Theme } from '@mui/material';

import { Cross } from '../../../icons';

export const getMuiChip = (theme: Theme): Components<Theme>['MuiChip'] => {
  const filledSecondaryLight = {
    backgroundColor: theme.palette.secondary.main,

    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },

    [`.${chipClasses.label}`]: {
      color: theme.palette.grey[900],
    },

    [`.${chipClasses.icon}`]: {
      color: theme.palette.grey[900],
    },

    [`.${chipClasses.deleteIcon}`]: {
      color: theme.palette.grey[900],

      '&:hover': {
        color: alpha(theme.palette.grey[900], 0.4),
      },
    },
  };

  const filledSecondaryDark = {
    backgroundColor: theme.palette.background.paper,

    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },

    [`.${chipClasses.label}`]: {
      color: theme.palette.secondary.main,
    },

    [`.${chipClasses.icon}`]: {
      color: theme.palette.grey[600],
    },

    [`.${chipClasses.deleteIcon}`]: {
      color: theme.palette.grey[600],

      '&:hover': {
        color: alpha(theme.palette.grey[600], 0.4),
      },
    },
  };

  return {
    defaultProps: {
      variant: 'filled',
      deleteIcon: <Cross />,
    },
    styleOverrides: {
      root: {
        border: 'none',
        width: 'fit-content',

        borderRadius: 120,

        backgroundColor: theme.palette.background.default,

        '&:hover': {
          backgroundColor: theme.palette.grey[200],
        },

        [`&&.${chipClasses.disabled}`]: {
          backgroundColor: theme.palette.grey[100],

          [`.${chipClasses.label}`]: {
            color: theme.palette.text.disabled,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.text.disabled,
          },

          [`.${chipClasses.deleteIcon}`]: {
            color: theme.palette.text.disabled,
          },
        },

        [`&:has(.${chipClasses.icon})`]: {
          display: 'flex',
          gap: theme.spacing(2),
        },
      },
      deletable: {
        display: 'flex',
        gap: theme.spacing(2),
      },
      label: {
        padding: 0,

        color: theme.palette.grey[900],

        fontStyle: 'normal',
      },
      deleteIcon: {
        margin: 0,
      },
      icon: {
        margin: 0,
      },

      outlined: {
        borderStyle: 'solid',
        borderColor: theme.palette.grey[400],

        [`.${chipClasses.icon}`]: {
          color: theme.palette.grey[600],
        },

        [`.${chipClasses.deleteIcon}`]: {
          color: theme.palette.grey[600],

          '&:hover': {
            color: alpha(theme.palette.grey[600], 0.4),
          },
        },

        [`&&.${chipClasses.disabled}`]: {
          borderColor: theme.palette.grey[400],

          backgroundColor: theme.palette.background.default,

          opacity: 'unset',

          [`.${chipClasses.label}`]: {
            color: theme.palette.grey[400],
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.grey[400],
          },

          [`.${chipClasses.deleteIcon}`]: {
            color: theme.palette.grey[400],
          },
        },

        [`&.${chipClasses.colorError}`]: {
          backgroundColor: theme.palette.error.light,
          borderColor: theme.palette.error.main,

          '&:hover': {
            backgroundColor: theme.palette.error.light,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.error.main,
          },
        },

        [`&.${chipClasses.colorInfo}`]: {
          backgroundColor: theme.palette.info.light,
          borderColor: theme.palette.info.main,

          '&:hover': {
            backgroundColor: theme.palette.info.light,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.info.main,
          },
        },

        [`&.${chipClasses.colorPrimary}`]: {
          backgroundColor: theme.palette.primary.light,
          borderColor: theme.palette.primary.main,

          '&:hover': {
            backgroundColor: theme.palette.primary.light,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.primary.main,
          },
        },

        [`&.${chipClasses.colorSecondary}`]: {
          backgroundColor: theme.palette.secondary.light,
          borderColor: theme.palette.secondary.main,

          '&:hover': {
            backgroundColor: theme.palette.secondary.light,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.secondary.main,
          },
        },

        [`&.${chipClasses.colorSuccess}`]: {
          backgroundColor: theme.palette.success.light,
          borderColor: theme.palette.success.main,

          '&:hover': {
            backgroundColor: theme.palette.success.light,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.success.main,
          },
        },

        [`&.${chipClasses.colorWarning}`]: {
          backgroundColor: theme.palette.warning.light,
          borderColor: theme.palette.warning.main,

          '&:hover': {
            backgroundColor: theme.palette.warning.light,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.warning.main,
          },
        },

        [`&.${chipClasses.disabled}`]: {
          borderColor: theme.palette.grey[400],
        },
      },

      filled: {
        '&:hover': {
          backgroundColor: theme.palette.grey[100],
        },

        [`.${chipClasses.icon}`]: {
          color: theme.palette.grey[900],
        },

        [`.${chipClasses.deleteIcon}`]: {
          color: theme.palette.grey[900],

          '&:hover': {
            color: alpha(theme.palette.grey[900], 0.4),
          },
        },

        [`&&.${chipClasses.disabled}`]: {
          backgroundColor: theme.palette.grey[100],

          [`.${chipClasses.label}`]: {
            color: theme.palette.text.disabled,
          },
        },

        [`&.${chipClasses.colorError}`]: {
          backgroundColor: theme.palette.error.main,

          '&:hover': {
            backgroundColor: theme.palette.error.dark,
          },

          [`.${chipClasses.label}`]: {
            color: theme.palette.error.contrastText,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.error.contrastText,
          },

          [`.${chipClasses.deleteIcon}`]: {
            color: theme.palette.error.contrastText,

            '&:hover': {
              color: alpha(theme.palette.error.contrastText, 0.4),
            },
          },
        },

        [`&.${chipClasses.colorInfo}`]: {
          backgroundColor: theme.palette.info.main,

          '&:hover': {
            backgroundColor: theme.palette.info.dark,
          },

          [`.${chipClasses.label}`]: {
            color: theme.palette.info.contrastText,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.info.contrastText,
          },

          [`.${chipClasses.deleteIcon}`]: {
            color: theme.palette.info.contrastText,

            '&:hover': {
              color: alpha(theme.palette.info.contrastText, 0.4),
            },
          },
        },

        [`&.${chipClasses.colorPrimary}`]: {
          backgroundColor: theme.palette.primary.main,

          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },

          [`.${chipClasses.label}`]: {
            color: theme.palette.primary.contrastText,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.primary.contrastText,
          },

          [`.${chipClasses.deleteIcon}`]: {
            color: theme.palette.primary.contrastText,

            '&:hover': {
              color: alpha(theme.palette.primary.contrastText, 0.4),
            },
          },
        },

        [`&.${chipClasses.colorSecondary}`]:
          theme.palette.mode === 'light'
            ? filledSecondaryLight
            : filledSecondaryDark,

        [`&.${chipClasses.colorSuccess}`]: {
          backgroundColor: theme.palette.success.main,

          '&:hover': {
            backgroundColor: theme.palette.success.dark,
          },

          [`.${chipClasses.label}`]: {
            color: theme.palette.success.contrastText,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.success.contrastText,
          },

          [`.${chipClasses.deleteIcon}`]: {
            color: theme.palette.success.contrastText,

            '&:hover': {
              color: alpha(theme.palette.success.contrastText, 0.4),
            },
          },
        },

        [`&.${chipClasses.colorWarning}`]: {
          backgroundColor: theme.palette.warning.main,

          '&:hover': {
            backgroundColor: theme.palette.warning.dark,
          },

          [`.${chipClasses.label}`]: {
            color: theme.palette.warning.contrastText,
          },

          [`.${chipClasses.icon}`]: {
            color: theme.palette.warning.contrastText,
          },

          [`.${chipClasses.deleteIcon}`]: {
            color: theme.palette.warning.contrastText,

            '&:hover': {
              color: alpha(theme.palette.warning.contrastText, 0.4),
            },
          },
        },
      },

      sizeSmall: {
        height: 32,
        padding: theme.spacing(1.5, 3),

        borderWidth: '1.5px',

        [`&.${chipClasses.deletable}`]: {
          padding: theme.spacing(1.5, 2, 1.5, 3),
        },

        [`&:has(.${chipClasses.icon})`]: {
          padding: theme.spacing(1.5, 2),
        },
      },
      labelSmall: {
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.43,
      },
      deleteIconSmall: {
        fontSize: 20,
      },
      iconSmall: {
        fontSize: 20,
      },

      sizeMedium: {
        height: 40,
        padding: theme.spacing(2, 4),

        borderWidth: '1.5px',

        [`&.${chipClasses.deletable}`]: {
          padding: theme.spacing(2, 3, 2, 4),
        },

        [`&:has(.${chipClasses.icon})`]: {
          padding: theme.spacing(2, 3),
        },
      },
      labelMedium: {
        fontSize: 16,
        fontWeight: 600,
        lineHeight: 1.5,
        letterSpacing: '-0.01em',
      },
      deleteIconMedium: {
        fontSize: 24,
      },
      iconMedium: {
        fontSize: 24,
      },
    },
    variants: [
      {
        props: { size: 'large' },
        style: {
          height: 48,
          padding: theme.spacing(3, 5),

          borderWidth: '2px',

          [`&.${chipClasses.deletable}`]: {
            padding: theme.spacing(3, 4, 3, 5),
          },

          [`&:has(.${chipClasses.icon})`]: {
            padding: theme.spacing(3, 4),
          },

          [`.${chipClasses.label}`]: {
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.5,
            letterSpacing: '-0.01em',
          },

          [`.${chipClasses.deleteIcon}`]: {
            fontSize: 24,
          },
        },
      },
    ],
  };
};
